import type { WsOutgoing, WsIncoming } from '../types/chatbot'

const RECONNECT_BASE_DELAY = 1000
const RECONNECT_MAX_DELAY = 30000
const RECONNECT_MAX_ATTEMPTS = 10
// Application-level heartbeat: the browser cannot send WebSocket ping frames
// from JS, so we send a {type:'ping'} message to keep the proxy connection
// alive even when the user is idle (DEV-212).
const PING_INTERVAL = 25000

type MessageHandler = (msg: WsIncoming) => void
type ConnectionHandler = () => void

export class ChatbotWebSocket {
  private ws: WebSocket | null = null
  private url: string
  private messageHandlers: MessageHandler[] = []
  private connectHandlers: ConnectionHandler[] = []
  private disconnectHandlers: ConnectionHandler[] = []
  private reconnectAttempts = 0
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null
  private pingTimer: ReturnType<typeof setInterval> | null = null
  private intentionalClose = false

  constructor(url: string) {
    this.url = url
  }

  get isConnected(): boolean {
    return this.ws?.readyState === WebSocket.OPEN
  }

  connect(): void {
    if (this.ws && (this.ws.readyState === WebSocket.OPEN || this.ws.readyState === WebSocket.CONNECTING)) {
      return
    }

    this.intentionalClose = false

    try {
      this.ws = new WebSocket(this.url)
    } catch (e) {
      console.error('[chatbot-ws] Failed to create WebSocket:', e)
      this.scheduleReconnect()
      return
    }

    this.ws.onopen = () => {
      this.reconnectAttempts = 0
      this.startPing()
      this.connectHandlers.forEach(h => h())
    }

    this.ws.onmessage = (event: MessageEvent) => {
      try {
        const msg: WsIncoming = JSON.parse(event.data)
        this.messageHandlers.forEach(h => h(msg))
      } catch (e) {
        console.error('[chatbot-ws] Failed to parse message:', e)
      }
    }

    this.ws.onclose = () => {
      this.stopPing()
      this.disconnectHandlers.forEach(h => h())
      if (!this.intentionalClose) {
        this.scheduleReconnect()
      }
    }

    this.ws.onerror = (e) => {
      console.error('[chatbot-ws] WebSocket error:', e)
    }
  }

  disconnect(): void {
    this.intentionalClose = true
    this.clearReconnectTimer()
    this.stopPing()
    if (this.ws) {
      this.ws.close(1000, 'client disconnect')
      this.ws = null
    }
  }

  send(msg: WsOutgoing): void {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      console.warn('[chatbot-ws] Cannot send, not connected')
      return
    }
    this.ws.send(JSON.stringify(msg))
  }

  onMessage(handler: MessageHandler): () => void {
    this.messageHandlers.push(handler)
    return () => {
      this.messageHandlers = this.messageHandlers.filter(h => h !== handler)
    }
  }

  onConnect(handler: ConnectionHandler): () => void {
    this.connectHandlers.push(handler)
    return () => {
      this.connectHandlers = this.connectHandlers.filter(h => h !== handler)
    }
  }

  onDisconnect(handler: ConnectionHandler): () => void {
    this.disconnectHandlers.push(handler)
    return () => {
      this.disconnectHandlers = this.disconnectHandlers.filter(h => h !== handler)
    }
  }

  private startPing(): void {
    this.stopPing()
    this.pingTimer = setInterval(() => {
      if (this.ws?.readyState === WebSocket.OPEN) {
        this.ws.send(JSON.stringify({ type: 'ping' }))
      }
    }, PING_INTERVAL)
  }

  private stopPing(): void {
    if (this.pingTimer) {
      clearInterval(this.pingTimer)
      this.pingTimer = null
    }
  }

  private scheduleReconnect(): void {
    if (this.reconnectAttempts >= RECONNECT_MAX_ATTEMPTS) {
      console.error('[chatbot-ws] Max reconnect attempts reached')
      return
    }

    const delay = Math.min(
      RECONNECT_BASE_DELAY * Math.pow(2, this.reconnectAttempts),
      RECONNECT_MAX_DELAY
    )
    this.reconnectAttempts++

    console.log(`[chatbot-ws] Reconnecting in ${delay}ms (attempt ${this.reconnectAttempts})`)

    this.reconnectTimer = setTimeout(() => {
      this.connect()
    }, delay)
  }

  private clearReconnectTimer(): void {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer)
      this.reconnectTimer = null
    }
  }
}

export function buildWsUrl(): string {
  const baseUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8001'
  const wsProtocol = baseUrl.startsWith('https') ? 'wss' : 'ws'
  const host = baseUrl.replace(/^https?:\/\//, '')
  return `${wsProtocol}://${host}/api/v1/chatbot/ws`
}
